import { useCallback, useEffect, useState } from "react";
import "./InquiriesPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useInquiries } from "../../../hooks/useInquiries";
import {
  deleteInquiryAsync,
  getNewInquiryCountAsync,
  updateInquiryStatusAsync,
} from "../../../api/InquiryApi";
import {
  ContactInquiry,
  ContactInquirySource,
  ContactInquiryStatus,
} from "../../../models/InquiryModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import Toast from "../../../components/Toast";
import { formatDateTime } from "../../../utils/DateTime";

const INQUIRY_STATUS_LABELS: Record<ContactInquiryStatus, string> = {
  [ContactInquiryStatus.New]: "New",
  [ContactInquiryStatus.Read]: "Read",
  [ContactInquiryStatus.Replied]: "Replied",
  [ContactInquiryStatus.Archived]: "Archived",
  [ContactInquiryStatus.Spam]: "Spam",
};

const INQUIRY_STATUS_CLASS: Record<ContactInquiryStatus, string> = {
  [ContactInquiryStatus.New]: "new",
  [ContactInquiryStatus.Read]: "read",
  [ContactInquiryStatus.Replied]: "replied",
  [ContactInquiryStatus.Archived]: "archived",
  [ContactInquiryStatus.Spam]: "spam",
};

const INQUIRY_SOURCE_LABELS: Record<ContactInquirySource, string> = {
  [ContactInquirySource.Website]: "Website",
  [ContactInquirySource.MobileApp]: "Mobile App",
  [ContactInquirySource.Other]: "Other",
};

interface IconProps {
  title?: string;
}

const ReadIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RepliedIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const ArchiveIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

const SpamIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const InquiriesPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canEditInquiries = hasPrivilege(OrgForms.Inquiries, "canEdit");
  const canDeleteInquiries = hasPrivilege(OrgForms.Inquiries, "canDelete");

  const { inquiries, loading, error, status, setStatus, reload } =
    useInquiries(organizationId);

  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const showToast = (
    message: string,
    type: "success" | "error" | "info",
  ) => setToast({ message, type });

  const refreshNewCount = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await getNewInquiryCountAsync(token, organizationId);
      if (res.success) setNewCount(res.result ?? 0);
    } catch (err) {
      console.error(err);
    }
  }, [organizationId, token]);

  useEffect(() => {
    refreshNewCount();
  }, [refreshNewCount]);

  const handleStatusChange = async (
    inquiry: ContactInquiry,
    nextStatus: ContactInquiryStatus,
  ) => {
    setSubmitting(true);
    try {
      const res = await updateInquiryStatusAsync(
        token,
        inquiry.id,
        nextStatus,
      );
      if (res.success) {
        await reload();
        await refreshNewCount();
        showToast(
          `Marked as ${INQUIRY_STATUS_LABELS[nextStatus]}`,
          "success",
        );
      } else {
        showToast(res.message || "Failed to update inquiry", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to update inquiry. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (inquiry: ContactInquiry) => {
    setSubmitting(true);
    try {
      const res = await deleteInquiryAsync(token, inquiry.id);
      if (res.success) {
        if (selectedInquiry?.id === inquiry.id) setSelectedInquiry(null);
        await reload();
        await refreshNewCount();
        showToast("Inquiry deleted successfully!", "success");
      } else {
        showToast(res.message || "Failed to delete inquiry", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to delete inquiry. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="inq-empty">Loading inquiries...</div>;
    }

    if (error) {
      return (
        <div className="inq-empty">
          <p>{error}</p>
          <button className="inq-btn inq-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (inquiries.length === 0) {
      return (
        <div className="inq-empty">
          {status === undefined
            ? "No inquiries yet. Messages sent through your website contact form will appear here."
            : `No ${INQUIRY_STATUS_LABELS[status].toLowerCase()} inquiries.`}
        </div>
      );
    }

    return (
      <div className="inq-table-wrap">
        <table className="inq-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Subject</th>
              <th>Received</th>
              <th>Source</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((inquiry) => (
              <tr
                key={inquiry.id}
                className="inq-row"
                onClick={() => setSelectedInquiry(inquiry)}
              >
                <td>
                  <button
                    className="inq-name"
                    onClick={() => setSelectedInquiry(inquiry)}
                  >
                    <span className="inq-avatar">
                      {inquiry.fullName.charAt(0).toUpperCase()}
                    </span>
                    {inquiry.fullName}
                  </button>
                </td>
                <td className="inq-contact">
                  {inquiry.email || (
                    <span className="inq-muted">—</span>
                  )}
                  {inquiry.phone && (
                    <span className="inq-phone">{inquiry.phone}</span>
                  )}
                </td>
                <td>
                  {inquiry.subject || (
                    <span className="inq-muted">—</span>
                  )}
                </td>
                <td>{formatDateTime(inquiry.createdAt)}</td>
                <td>
                  <span className="inq-source">
                    {INQUIRY_SOURCE_LABELS[inquiry.source] ?? inquiry.source}
                  </span>
                </td>
                <td>
                  <span
                    className={`inq-badge ${INQUIRY_STATUS_CLASS[inquiry.status]}`}
                  >
                    {INQUIRY_STATUS_LABELS[inquiry.status]}
                  </span>
                </td>
                <td>
                  <div className="inq-actions">
                    {canEditInquiries &&
                      inquiry.status === ContactInquiryStatus.New && (
                    <button
                      className="inq-icon-btn inq-icon-btn--read"
                      title="Mark as read"
                      aria-label={`Mark ${inquiry.fullName}'s inquiry as read`}
                      disabled={submitting}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(
                          inquiry,
                          ContactInquiryStatus.Read,
                        );
                      }}
                    >
                      <ReadIcon />
                    </button>
                  )}
                  {canEditInquiries &&
                    inquiry.status !== ContactInquiryStatus.Replied && (
                    <button
                      className="inq-icon-btn inq-icon-btn--replied"
                      title="Mark as replied"
                      aria-label={`Mark ${inquiry.fullName}'s inquiry as replied`}
                      disabled={submitting}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(
                          inquiry,
                          ContactInquiryStatus.Replied,
                        );
                      }}
                    >
                      <RepliedIcon />
                    </button>
                  )}
                  {canEditInquiries &&
                    inquiry.status !== ContactInquiryStatus.Archived &&
                    inquiry.status !== ContactInquiryStatus.Spam && (
                      <button
                        className="inq-icon-btn inq-icon-btn--archive"
                        title="Archive"
                        aria-label={`Archive ${inquiry.fullName}'s inquiry`}
                        disabled={submitting}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(
                            inquiry,
                            ContactInquiryStatus.Archived,
                          );
                        }}
                      >
                        <ArchiveIcon />
                      </button>
                    )}
                  {canEditInquiries && (
                    <button
                      className="inq-icon-btn inq-icon-btn--spam"
                      title="Mark as spam"
                      aria-label={`Mark ${inquiry.fullName}'s inquiry as spam`}
                      disabled={submitting}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(inquiry, ContactInquiryStatus.Spam);
                      }}
                    >
                      <SpamIcon />
                    </button>
                  )}
                  {canDeleteInquiries && (
                    <button
                      className="inq-icon-btn inq-icon-btn--delete"
                      title="Delete"
                      aria-label={`Delete ${inquiry.fullName}'s inquiry`}
                      disabled={submitting}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            `Delete inquiry from "${inquiry.fullName}"?`,
                          )
                        ) {
                          handleDelete(inquiry);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="inq-page">
      <div className="inq-header">
        <div>
          <h2>Inquiries</h2>
          <p>
            Messages received through your website contact form for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        {newCount > 0 && (
          <div className="inq-new-badge">
            {newCount} new {newCount === 1 ? "inquiry" : "inquiries"}
          </div>
        )}
      </div>

      <div className="inq-filters">
        <label className="inq-filter">
          <span>Status</span>
          <select
            value={status === undefined ? "" : String(status)}
            onChange={(e) =>
              setStatus(
                e.target.value
                  ? (Number(e.target.value) as ContactInquiryStatus)
                  : undefined,
              )
            }
          >
            <option value="">All statuses</option>
            {Object.entries(INQUIRY_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="inq-btn inq-btn--secondary"
          onClick={() => setStatus(undefined)}
        >
          Reset
        </button>
      </div>

      {renderBody()}

      {selectedInquiry && (
        <div
          className="inq-modal-overlay"
          onClick={() => setSelectedInquiry(null)}
        >
          <div
            className="inq-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Inquiry details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inq-modal__header">
              <div>
                <h3>{selectedInquiry.fullName}</h3>
                <p className="inq-modal__meta">
                  {selectedInquiry.email || selectedInquiry.phone || "No contact info"}
                  {" · "}
                  {formatDateTime(selectedInquiry.createdAt)}
                  {" · "}
                  {INQUIRY_SOURCE_LABELS[selectedInquiry.source] ?? "Website"}
                </p>
              </div>
              <span
                className={`inq-badge ${INQUIRY_STATUS_CLASS[selectedInquiry.status]}`}
              >
                {INQUIRY_STATUS_LABELS[selectedInquiry.status]}
              </span>
            </div>
            {selectedInquiry.subject && (
              <p className="inq-modal__subject">{selectedInquiry.subject}</p>
            )}
            <p className="inq-modal__message">{selectedInquiry.message}</p>
            <div className="inq-modal__footer">
              <button
                className="inq-btn inq-btn--secondary"
                onClick={() => setSelectedInquiry(null)}
              >
                Close
              </button>
{canDeleteInquiries && (
                <button
                  className="inq-btn inq-btn--delete"
                  disabled={submitting}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete inquiry from "${selectedInquiry.fullName}"?`,
                      )
                    ) {
                      handleDelete(selectedInquiry);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default InquiriesPage;
